import { useContext, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import CircularProgress from "@mui/material/CircularProgress";
// Context
import { AlertNotificationContext } from "../../contexts/AlertNotificationContext";
// State
import { useUserBranches } from "../../state/userBranchListState";
import { useStaffCardContractList } from "../../state/staffCardContractListState";
import { useStaffCardWardrobeList } from "../../state/staffCardWardrobeListState";
import { useMain } from "../../state/userMainState";
// Hooks
import { useStaffUpdate } from "../../hooks/useStaffUpdate";
import { useCheckAPI } from "../../hooks/useCheckAPI";
// Components
import { RHFSelectField } from "../../components/RHFSelectField";
// MUI
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";

export const BulkStaffEditDialog = ({ open, onClose, staffData }) => {
  const { setAlertNotification } = useContext(AlertNotificationContext);
  const queryClient = useQueryClient();

  // This component stays mounted while `staffData` is still loading, so every
  // hook below has to run unconditionally - returning early here would change
  // the hook count between renders and crash with "Rendered more hooks than
  // during the previous render". The guard lives just above the JSX instead.
  const currentStaff = staffData?.staff;

  // `values` rather than `defaultValues`: defaultValues are only read on the
  // first render, which for this component happens before the staff query
  // resolves, leaving the form permanently blank.
  const values = useMemo(
    () => ({
      staffid: currentStaff?.staffid ?? "",
      branch: currentStaff?.branch ?? "",
      contract: currentStaff?.contract ?? "",
      uniform: currentStaff?.uniform ?? "",
      gender: currentStaff?.gender ?? "",
    }),
    [currentStaff],
  );

  const { control, handleSubmit } = useForm({ values });

  const { data: dataBranches, isSuccess: branchesReady } = useUserBranches();
  const branchArray = [
    ...(dataBranches?.data.data.data.list.branch || []),
  ].sort((a, b) => (a.name > b.name ? 1 : -1));

  const { data: dataContracts, isSuccess: contractsReady } =
    useStaffCardContractList();
  const contractArray = [
    ...(dataContracts?.data.data.data.list.contract || []),
  ].sort((a, b) => (a.description > b.description ? 1 : -1));

  const { data: dataWardrobes, isSuccess: wardrobesReady } =
    useStaffCardWardrobeList();
  const wardrobeArray = [
    ...(dataWardrobes?.data.data.data.list.wardrobe || []),
  ].sort((a, b) => (a.description > b.description ? 1 : -1));

  const { data: mainDataResponse, isSuccess: bodyTypesReady } = useMain();
  const bodyTypes = mainDataResponse?.data?.data?.data?.data?.bodyTypes || [];

  const optionsReady =
    branchesReady && contractsReady && wardrobesReady && bodyTypesReady;

  const {
    mutate: updateStaffSave,
    isSuccess: updateStaffSuccess,
    data: updateStaffData,
  } = useStaffUpdate();

  const onSubmit = (formData) => {
    updateStaffSave({
      data: {
        filter: { f_staffId: formData.staffid },
        staff: { ...currentStaff, ...formData },
      },
    });
  };

  const updateStaffSuccessFunc = () => {
    setAlertNotification({
      message: "Staff member updated",
      severity: "success",
    });
    queryClient.invalidateQueries({ queryKey: ["draftOrder"] });
    queryClient.invalidateQueries({ queryKey: ["draftOrderStaff"] });
    queryClient.invalidateQueries({ queryKey: ["wardrobeCard"] });
    onClose();
  };

  const updateStaffErrorFunc = () => {
    setAlertNotification({
      message: `${updateStaffData.data.statusText}`,
      severity: "warning",
    });
  };

  useCheckAPI(
    updateStaffSuccess,
    updateStaffData,
    updateStaffData?.data?.status,
    updateStaffSuccessFunc,
    updateStaffErrorFunc,
  );

  const validationHelper = {
    branch: { required: "Branch is required" },
    contract: { required: "Contract is required" },
    uniform: { required: "Wardrobe is required" },
    gender: { required: "Item Types is required" },
  };

  if (!currentStaff) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit staff details</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {!optionsReady && (
              <CircularProgress size={24} sx={{ alignSelf: "center" }} />
            )}
            {optionsReady && (
              <>
                <RHFSelectField
                  name="branch"
                  label="Branch"
                  control={control}
                  options={branchArray}
                  optionIdentifiers={{ id: "no", label: "name" }}
                  validationHelper={validationHelper}
                  rules={{ required: true }}
                  viewMode={false}
                />
                <RHFSelectField
                  name="contract"
                  label="Contract"
                  control={control}
                  options={contractArray}
                  optionIdentifiers={{ id: "no", label: "description" }}
                  validationHelper={validationHelper}
                  rules={{ required: true }}
                  viewMode={false}
                />
                <RHFSelectField
                  name="uniform"
                  label="Wardrobe"
                  control={control}
                  options={wardrobeArray}
                  optionIdentifiers={{ id: "id", label: "description" }}
                  validationHelper={validationHelper}
                  rules={{ required: true }}
                  viewMode={false}
                />
                <RHFSelectField
                  name="gender"
                  label="Item Types"
                  control={control}
                  options={bodyTypes}
                  optionIdentifiers={{ id: "code", label: "description" }}
                  validationHelper={validationHelper}
                  rules={{ required: true }}
                  viewMode={false}
                />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
